import { ContractNegotiationVersion } from '../../models/Entities/Contracts/ContractNegotiationVersion';
import { NewContractNegotiationVersion } from '../../models/Entities/Contracts/NewContractNegotiationVersion';
import { ErrorType } from '../../models/HttpError';
import * as Services from '../../services/ContractNegotiationVersion.Services';
import * as Actions from '../actions';
import { AppThunkAction } from '../reducers';

type KnownAction =
  | Actions.ContractNegotiationVersion.ContractNegotiationVersion_GetAll_Request_Action
  | Actions.ContractNegotiationVersion.ContractNegotiationVersion_GetAll_Receive_Action
  | Actions.ContractNegotiationVersion.ContractNegotiationVersion_GetAll_Error_Action
  | Actions.ContractNegotiationVersion.ContractNegotiationVersion_GetById_Request_Action
  | Actions.ContractNegotiationVersion.ContractNegotiationVersion_GetById_Receive_Action
  | Actions.ContractNegotiationVersion.ContractNegotiationVersion_GetById_Error_Action
  | Actions.ContractNegotiationVersion.ContractNegotiationVersion_Create_Request_Action
  | Actions.ContractNegotiationVersion.ContractNegotiationVersion_Create_Receive_Action
  | Actions.ContractNegotiationVersion.ContractNegotiationVersion_Create_Error_Action
  | Actions.ContractNegotiationVersion.ContractNegotiationVersion_Update_Request_Action
  | Actions.ContractNegotiationVersion.ContractNegotiationVersion_Update_Receive_Action
  | Actions.ContractNegotiationVersion.ContractNegotiationVersion_Update_Error_Action
  | Actions.ContractNegotiationVersion.ContractNegotiationVersion_Delete_Request_Action
  | Actions.ContractNegotiationVersion.ContractNegotiationVersion_Delete_Receive_Action
  | Actions.ContractNegotiationVersion.ContractNegotiationVersion_Delete_Error_Action;

const GetUnauthorizedError = (): ErrorType => ({
  ErrorCode: 401,
  ErrorMessage: 'Not authorized',
  Errors: [],
});

export const actionCreators = {
  GetContractNegotiationVersions:
    (contractNegotiationId: string, contractId: number): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const state = getState();
      const token = state.oidc?.user?.access_token;

      if (!token) {
        dispatch({ type: 'CONTRACTNEGOTIATIONVERSION_GET_ALL_ERROR_ACTION', contractNegotiationId, contractId, error: GetUnauthorizedError() });
        return;
      }

      dispatch({ type: 'CONTRACTNEGOTIATIONVERSION_GET_ALL_REQUEST_ACTION', contractNegotiationId, contractId });

      Services.GetContractNegotiationVersions(contractNegotiationId, token)
        .then((contractNegotiationVersions: ContractNegotiationVersion[]) => {
          dispatch({ type: 'CONTRACTNEGOTIATIONVERSION_GET_ALL_RECEIVE_ACTION', contractNegotiationVersions, contractNegotiationId, contractId });
        })
        .catch((error: ErrorType) =>
          dispatch({ type: 'CONTRACTNEGOTIATIONVERSION_GET_ALL_ERROR_ACTION', contractNegotiationId, contractId, error })
        );
    },

  GetContractNegotiationVersionById:
    (id: string, contractId: number): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const state = getState();
      const token = state.oidc?.user?.access_token;

      if (!token) {
        dispatch({ type: 'CONTRACTNEGOTIATIONVERSION_GET_BY_ID_ERROR_ACTION', id, contractId, error: GetUnauthorizedError() });
        return;
      }

      dispatch({ type: 'CONTRACTNEGOTIATIONVERSION_GET_BY_ID_REQUEST_ACTION', id, contractId });

      Services.GetContractNegotiationVersionById(id, token)
        .then((contractNegotiationVersion: ContractNegotiationVersion) => {
          dispatch({ type: 'CONTRACTNEGOTIATIONVERSION_GET_BY_ID_RECEIVE_ACTION', contractNegotiationVersion, contractId });
        })
        .catch((error: ErrorType) =>
          dispatch({ type: 'CONTRACTNEGOTIATIONVERSION_GET_BY_ID_ERROR_ACTION', id, contractId, error })
        );
    },

  CreateContractNegotiationVersion:
    (contractNegotiationVersion: NewContractNegotiationVersion, contractId: number): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const state = getState();
      const token = state.oidc?.user?.access_token;

      if (!token) {
        dispatch({ type: 'CONTRACTNEGOTIATIONVERSION_CREATE_ERROR_ACTION', contractNegotiationVersion, contractId, error: GetUnauthorizedError() });
        return;
      }

      dispatch({ type: 'CONTRACTNEGOTIATIONVERSION_CREATE_REQUEST_ACTION', contractNegotiationVersion, contractId });

      Services.CreateContractNegotiationVersion(contractNegotiationVersion, token)
        .then((saved: ContractNegotiationVersion) => {
          dispatch({ type: 'CONTRACTNEGOTIATIONVERSION_CREATE_RECEIVE_ACTION', contractNegotiationVersion: saved, contractId });
        })
        .catch((error: ErrorType) =>
          dispatch({ type: 'CONTRACTNEGOTIATIONVERSION_CREATE_ERROR_ACTION', contractNegotiationVersion, contractId, error })
        );
    },

  UpdateContractNegotiationVersion:
    (contractNegotiationVersion: ContractNegotiationVersion, contractId: number): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const state = getState();
      const token = state.oidc?.user?.access_token;

      if (!token) {
        dispatch({ type: 'CONTRACTNEGOTIATIONVERSION_UPDATE_ERROR_ACTION', contractNegotiationVersion, contractId, error: GetUnauthorizedError() });
        return;
      }

      dispatch({ type: 'CONTRACTNEGOTIATIONVERSION_UPDATE_REQUEST_ACTION', contractNegotiationVersion, contractId });

      Services.UpdateContractNegotiationVersion(contractNegotiationVersion, token)
        .then((updated: ContractNegotiationVersion) => {
          dispatch({ type: 'CONTRACTNEGOTIATIONVERSION_UPDATE_RECEIVE_ACTION', contractNegotiationVersion: updated, contractId });
        })
        .catch((error: ErrorType) =>
          dispatch({ type: 'CONTRACTNEGOTIATIONVERSION_UPDATE_ERROR_ACTION', contractNegotiationVersion, contractId, error })
        );
    },

  DeleteContractNegotiationVersion:
    (id: string, contractId: number, contractNegotiationId: string): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const state = getState();
      const token = state.oidc?.user?.access_token;

      if (!token) {
        dispatch({ type: 'CONTRACTNEGOTIATIONVERSION_DELETE_ERROR_ACTION', id, contractId, contractNegotiationId, error: GetUnauthorizedError() });
        return;
      }

      dispatch({ type: 'CONTRACTNEGOTIATIONVERSION_DELETE_REQUEST_ACTION', id, contractId, contractNegotiationId });

      Services.DeleteContractNegotiationVersion(id, token)
        .then(() => {
          dispatch({ type: 'CONTRACTNEGOTIATIONVERSION_DELETE_RECEIVE_ACTION', id, contractId, contractNegotiationId });
        })
        .catch((error: ErrorType) =>
          dispatch({ type: 'CONTRACTNEGOTIATIONVERSION_DELETE_ERROR_ACTION', id, contractId, contractNegotiationId, error })
        );
    },
};
